/**
 * ErrorModal
 * Centered modal for displaying action-blocking errors (e.g. API failures).
 * User dismisses the modal and can retry the action themselves.
 *
 * Follows the Content + Wrapper split pattern used by AIConsentModal and
 * CountryPickerModal so Storybook can render the content without a native
 * Modal overlay.
 */

import React from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_SM, FS_MD, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorModalContentProps {
  /** Translated error message to display */
  message: string;
  /** Called when user taps OK */
  onDismiss: () => void;
  /** Renders without Modal overlay — for Storybook */
  embedded?: boolean;
}

export interface ErrorModalProps extends Omit<ErrorModalContentProps, 'embedded'> {
  visible: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ICON_SIZE = 48;
const BUTTON_HEIGHT = 44;

// ---------------------------------------------------------------------------
// Content (presentational — no native Modal)
// ---------------------------------------------------------------------------

export function ErrorModalContent({
  message,
  onDismiss,
  embedded = false,
}: ErrorModalContentProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const buttonHeight = Math.round(BUTTON_HEIGHT * fontScale);

  const card = (
    <View
      testID="error-modal"
      accessibilityRole="alert"
      style={{
        backgroundColor: theme.card,
        borderRadius: 16,
        padding: 24,
        marginHorizontal: 32,
        alignItems: 'center',
        maxWidth: 360,
        width: '100%',
      }}
    >
      {/* Error icon */}
      <View
        style={{
          width: ICON_SIZE,
          height: ICON_SIZE,
          borderRadius: ICON_SIZE / 2,
          backgroundColor: theme.errorBg,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: 24,
            fontWeight: '700',
            color: colors.error,
          }}
        >
          !
        </Text>
      </View>

      {/* Title */}
      <Text
        allowFontScaling={false}
        accessibilityRole="header"
        style={{
          fontSize: FS_LG * fontScale,
          fontWeight: '700',
          color: theme.text,
          textAlign: 'center',
          marginBottom: 8,
          ...ffs,
        }}
      >
        {t('common.error')}
      </Text>

      {/* Message */}
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_SM * fontScale,
          color: theme.textSecondary,
          textAlign: 'center',
          lineHeight: Math.round(FS_SM * fontScale * 1.5),
          marginBottom: 24,
          ...ffs,
        }}
      >
        {message}
      </Text>

      {/* OK button */}
      <TouchableOpacity
        testID="error-modal-dismiss"
        accessibilityLabel={t('common.ok')}
        accessibilityRole="button"
        onPress={onDismiss}
        activeOpacity={0.7}
        style={{
          width: '100%',
          minHeight: buttonHeight,
          borderRadius: buttonHeight / 2,
          backgroundColor: colors.primary,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: '600',
            color: '#FFFFFF',
            ...ffs,
          }}
        >
          {t('common.ok')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  if (embedded) return card;

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {card}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper (for app use)
// ---------------------------------------------------------------------------

export function ErrorModal({
  visible,
  message,
  onDismiss,
}: ErrorModalProps) {
  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onDismiss}
    >
      <ErrorModalContent
        message={message}
        onDismiss={onDismiss}
      />
    </Modal>
  );
}
